// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
/* Starter Custom Code
import {
    HasProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
*/
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
// import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage"; // Store Custom Code
// import Button, { ButtonPresentationProps } from "@insite/mobius/Button"; // Starter Custom Code
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
// import React, { useContext, useState } from "react"; // Starter Custom Code
import { connect, ResolveThunks } from "react-redux";

/* Starter Custom Code - Start */
import React, { useContext, useMemo, useState } from "react";
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import PlusCircleFill from "@store/Library/Components/Toolkit/Icons/PlusCircle/PlusCircleFill";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { useCanAddToCart, useHasEnoughInventory } from "@toolkit/Framework/Store/Data/Products/ProductsHooks";
import { css } from "styled-components";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { IconWrapper } from "@store/MobiusOverrides/Icon/Icon";

/* Store Custom Code - Start */
import openMinicartDrawer from "@store/Framework/Store/Store/Components/MinicartDrawer/Handlers/openMinicartDrawer";
import AddToCartModal from "@store/Library/Components/AddToCartModal";
/* Store Custom Code - End */

export interface ProductAddToCartButtonProps
    extends Omit<OwnProps, keyof ButtonPresentationProps | keyof LinkPresentationProps> {}

export const useShowProductAddToCartButton = (
    configurationCompleted: boolean | undefined,
    variantSelectionCompleted: boolean | undefined,
    // Store Custom Code
    allowDisabledWhenConfigurationIncomplete?: boolean,
) => {
    const productContext = useContext(ProductContext);
    const { product, productInfo } = productContext;
    const hasEnoughInventory = useHasEnoughInventory(productContext);

    /* Store Custom Code - Start */
    if (allowDisabledWhenConfigurationIncomplete) {
        return hasEnoughInventory || productInfo.failedToLoadInventory;
    }
    /* Store Custom Code - End */

    const canAddToCart = useCanAddToCart(product, productInfo, configurationCompleted, variantSelectionCompleted);

    return !((!hasEnoughInventory && !productInfo.failedToLoadInventory) || !canAddToCart);
};
/* Starter Custom Code - End */

// Store Custom Code - Start (DCNE-2540)
/** Pure so it can be unit tested without rendering the component. Preserves every existing disabling reason. */
export const computeIsAddToCartDisabled = ({
    qtyOrdered,
    isAddToCartButtonDisabled,
    shouldDisableForIncompleteConfiguration,
    hasIncompatibleSpecialUnitRestriction,
}: {
    qtyOrdered: number;
    isAddToCartButtonDisabled?: boolean;
    shouldDisableForIncompleteConfiguration?: boolean;
    hasIncompatibleSpecialUnitRestriction?: boolean;
}) =>
    qtyOrdered <= 0 ||
    !!isAddToCartButtonDisabled ||
    !!shouldDisableForIncompleteConfiguration ||
    !!hasIncompatibleSpecialUnitRestriction;
// Store Custom Code - End

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ProductAddToCartButtonStyles; // Starter Custom Code
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    // Starter Custom Code
    overridedStyles?: ProductAddToCartButtonStyles;
    isLink?: boolean;
    showButtonIconOnly?: boolean;
    // Store Custom Code
    useModal?: boolean;
    inModal?: boolean;
    allowDisabledWhenConfigurationIncomplete?: boolean;
    hasIncompatibleSpecialUnitRestriction?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        // Store Removed Code - cartSettings
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        // Store Removed Code - addToCartPopupTimeout
        isUpdatingCart, // Starter Custom Code
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    openMinicartDrawer, // Store Custom Code
};

/* Starter Custom Code - Start */
export interface ProductAddToCartButtonStyles {
    button?: ButtonPresentationProps;
    link?: LinkPresentationProps;
}

export const productAddToCartButtonStyles: ProductAddToCartButtonStyles = {
    button: {
        variant: "secondary" /* Store Custom Code */,
        css: css`
            ${x => x.theme.button.secondary.css}/* Store Custom Code */
        `,
    },
    link: linkGetActionLinkProps(PlusCircleFill),
};
/* Starter Custom Code - End */

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    // Store Removed Code - addToCartPopupTimeout
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    overridedStyles, // Starter Custom Code
    isLink, // Starter Custom Code
    isUpdatingCart, // Starter Custom Code
    showButtonIconOnly, // Starter Custom Code
    useModal, // Store Custom Code
    inModal, // Store Custom Code
    openMinicartDrawer, // Store Custom Code
    productContext, // Store Custom Code
    allowDisabledWhenConfigurationIncomplete, // Store Custom Code
    hasIncompatibleSpecialUnitRestriction, // Store Custom Code
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);

    /* Starter Custom Code - Start */
    const trackAddToCart = ga.use.trackAddToCart();
    const styles = useExpandProps(productAddToCartButtonStyles, extendedStyles, overridedStyles);
    const [isLoading, setIsLoading] = useState(false);

    /* Store Custom Code - Start */
    const shouldDisableForIncompleteConfiguration =
        allowDisabledWhenConfigurationIncomplete && configurationCompleted === false;

    const isDisabled = computeIsAddToCartDisabled({
        qtyOrdered,
        isAddToCartButtonDisabled,
        shouldDisableForIncompleteConfiguration,
        hasIncompatibleSpecialUnitRestriction,
    });
    /* Store Custom Code - End */

    const buttonStyles = useMemo(() => {
        if (showButtonIconOnly) {
            return mergeProps<ProductAddToCartButtonStyles["button"]>(styles.button, {
                css: css`
                    ${IconWrapper} {
                        margin: initial;
                    }
                `,
            });
        }
        return styles.button;
    }, [showButtonIconOnly, styles.button]);
    /* Starter Custom Code - End */
    /* Store Custom Code - Start */
    const [showAddToCartModal, setShowAddToCartModal] = useState(false);
    const [returnedCartLine, setReturnedCartLine] = useState<CartLineModel[]>([]);
    /* Store Custom Code - End */

    if (
        !useShowProductAddToCartButton(
            configurationCompleted,
            variantSelectionCompleted,
            /* Store Custom Code */
            allowDisabledWhenConfigurationIncomplete,
        ) /* Starter Custom Code */
    ) {
        return null;
    }

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();
        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        setIsLoading(true); // Starter Custom Code

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
            /* Store Custom Code - Start */
            onError(error) {
                toasterContext.addToast({ body: error, messageType: "danger", timeoutLength: 6000 });
            },
            /* Store Custom Code - End */
        })) as CartLineModel;

        setIsLoading(false); // Starter Custom Code

        /* Store Custom Code - Start */
        if (productContext.product.configurationType === "Standard") {
            const configuration = productContext.product.detail?.configuration;
            const configurationProducts =
                configuration && configuration.configSections
                    ? configuration.configSections.reduce((acc, section) => {
                          if (section.sectionOptions) {
                              section.sectionOptions.forEach(option => {
                                  if (
                                      configurationSelection &&
                                      configurationSelection[section.id] === option.id &&
                                      option &&
                                      !acc.includes(option)
                                  ) {
                                      if (option.name === "Empty") {
                                          return;
                                      }
                                      acc.push(option);
                                  }
                              });
                          }
                          return acc;
                      }, [] as any[])
                    : [];

            trackAddToCart({ items: configurationProducts });
        } else {
            trackAddToCart();
        }
        /* Store Custom Code - End */
        /* Store Custom Code - Start */
        if (!cartline) {
            return;
        }

        // Store Removed Code - addToast

        if (showAddToCartConfirmationDialog && !inModal) {
            setShowAddToCartModal(true);
            setReturnedCartLine([cartline]);
        }
        openMinicartDrawer();
        /* Store Custom Code - End */
    };

    return (
        <>
            {/* Starter Custom Code - Start */}
            {isLink ? (
                <Link
                    {...styles.link}
                    onClick={addToCartClickHandler}
                    disabled={qtyOrdered <= 0 || isUpdatingCart}
                    {...otherProps}
                >
                    {labelOverride ?? translate("Add to Cart")}
                </Link>
            ) : (
                <Button
                    {...buttonStyles}
                    onClick={addToCartClickHandler}
                    disabled={isDisabled} /* Store Custom Code */
                    isLoading={isLoading}
                    {...otherProps}
                >
                    {!showButtonIconOnly && <span>{labelOverride ?? translate("Add to Cart")}</span>}
                    {showButtonIconOnly ? (
                        <VisuallyHidden>{labelOverride ?? translate("Add to Cart")}</VisuallyHidden>
                    ) : null}
                </Button>
            )}
            {/* Starter Custom Code - End */}
            {/* Store Custom Code - Start */}
            {useModal && (
                <AddToCartModal
                    modalIsOpen={showAddToCartModal}
                    onClose={() => {
                        setShowAddToCartModal(false);
                        setReturnedCartLine([]);
                    }}
                    returnedCartLine={returnedCartLine}
                ></AddToCartModal>
            )}
            {/* Store Custom Code - End */}
        </>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
